import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';

interface UserData {
    id: string;
    username: string;
    email: string;
    role: 'USER' | 'ADMIN';
    balance: number;
    password?: string; // Apenas para o formulário
}

export default function AdminUserEditPage() {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { showNotification } = useNotificationDispatch();

    const [user, setUser] = useState<Partial<UserData>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchUser = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await apiClient.get(`/admin/users/${userId}`);
            setUser(response.data);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Usuário não encontrado.', 'error');
            navigate('/admin/users');
        } finally {
            setLoading(false);
        }
    }, [userId, navigate, showNotification]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        const payload = { ...user };
        // Garante que a senha só seja enviada se for alterada
        if (!payload.password) {
            delete payload.password;
        }

        try {
            await apiClient.put(`/admin/users/${userId}`, payload);
            showNotification('Usuário atualizado com sucesso!', 'success');
            setTimeout(() => navigate('/admin/users'), 1500);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao atualizar o usuário.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <p>Carregando dados do usuário...</p>;
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Editar Usuário</h1>
                <Link to="/admin/users" className="px-4 py-2 text-sm font-semibold text-text-secondary bg-white border border-border-color rounded-lg hover:bg-gray-50">
                    &larr; Voltar para a lista
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-subtle max-w-2xl space-y-6">
                <div>
                    <label htmlFor="username" className="form-label">Nome de Usuário</label>
                    <input type="text" id="username" name="username" value={user.username || ''} onChange={handleChange} required className="form-input" />
                </div>

                <div>
                    <label htmlFor="email" className="form-label">Email</label>
                    <input type="email" id="email" name="email" value={user.email || ''} onChange={handleChange} required className="form-input" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="role" className="form-label">Cargo (Role)</label>
                        <select id="role" name="role" value={user.role || 'USER'} onChange={handleChange} className="form-input">
                            <option value="USER">USER</option>
                            <option value="ADMIN">ADMIN</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="balance" className="form-label">Saldo (R$)</label>
                        <input type="number" id="balance" name="balance" value={user.balance || 0} onChange={handleChange} step="0.01" className="form-input" />
                    </div>
                </div>

                <div>
                    <label htmlFor="password" className="form-label">Nova Senha</label>
                    <input type="password" id="password" name="password" value={user.password || ''} onChange={handleChange} className="form-input" placeholder="Deixe em branco para não alterar" />
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t border-border-color">
                    <Link to="/admin/users" className="px-5 py-2.5 text-sm font-semibold text-text-secondary bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                        Cancelar
                    </Link>
                    <button type="submit" disabled={saving} className="px-6 py-2.5 text-sm font-semibold text-white bg-brand-blue rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">
                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </form>
        </div>
    );
}